'use client'

import { useState, useRef, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { ArrowLeft, Send, Bot, User } from 'lucide-react'
import Link from 'next/link'

interface Message { role: 'user' | 'assistant'; content: string }

const SUGGESTIONS = [
  'قداه عندي فلوس اليوم؟',
  'آش الأرتيكل اللي باع برشا؟',
  'Articles avec stock faible?',
  'Combien j\'ai gagné ce mois?',
  'قداه الستوك متاعي؟',
]

export default function ChatPage() {
  const { session } = useApp()
  const router = useRouter()
  const bottomRef = useRef<HTMLDivElement>(null)

  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: 'Ahla! Ana hna bech n3awnek. Is2elni 3la 7wentk - caisse, stoke, bénéfices, kol 7aja! 😊' }
  ])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => { if (!session) router.replace('/login') }, [session])
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [messages])

  async function sendMessage(text?: string) {
    const msg = text || input.trim()
    if (!msg || loading) return
    setInput('')
    setMessages(m => [...m, { role: 'user', content: msg }])
    setLoading(true)
    try {
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: msg,
          shop_id: session!.shop.id,
          user_role: session!.user.role,
          history: messages.slice(-6),
        }),
      })
      const data = await res.json()
      setMessages(m => [...m, { role: 'assistant', content: data.response }])
    } catch {
      setMessages(m => [...m, { role: 'assistant', content: 'Mochkla fel connexion, 3aawed essayez!' }])
    }
    setLoading(false)
  }

  return (
    <div className="flex flex-col h-screen">
      <div className="bg-white shadow-sm flex-shrink-0">
        <div className="flex items-center gap-3 px-4 pt-12 pb-4">
          <Link href="/dashboard" className="p-2 -ml-2 text-gray-600"><ArrowLeft className="w-6 h-6" /></Link>
          <div className="flex items-center gap-2 flex-1">
            <div className="w-9 h-9 bg-indigo-100 rounded-full flex items-center justify-center">
              <Bot className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">AI Mta3 7wentk</p>
              <p className="text-xs text-green-500">● En ligne</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === 'assistant' ? 'bg-indigo-100' : 'bg-gray-200'}`}>
              {m.role === 'assistant' ? <Bot className="w-4 h-4 text-indigo-600" /> : <User className="w-4 h-4 text-gray-600" />}
            </div>
            <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
              m.role === 'assistant' ? 'bg-white border border-gray-100 shadow-sm text-gray-800 rounded-tl-sm' : 'bg-indigo-600 text-white rounded-tr-sm'
            }`}>{m.content}</div>
          </div>
        ))}
        {loading && (
          <div className="flex gap-2">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center"><Bot className="w-4 h-4 text-indigo-600" /></div>
            <div className="bg-white border border-gray-100 shadow-sm px-4 py-3 rounded-2xl rounded-tl-sm">
              <div className="flex gap-1">
                {[0,150,300].map(d => <div key={d} className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {messages.length <= 1 && (
        <div className="px-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar flex-shrink-0">
          {SUGGESTIONS.map(s => (
            <button key={s} onClick={() => sendMessage(s)}
              className="flex-shrink-0 px-3 py-2 bg-white border border-gray-200 rounded-2xl text-sm text-gray-700 hover:border-indigo-400 active:scale-95">
              {s}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white border-t border-gray-100 px-4 py-3 flex-shrink-0">
        <div className="flex gap-2">
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder="Is2el 3la 7wentk..."
            className="flex-1 px-4 py-3 bg-gray-50 rounded-2xl border border-gray-200 outline-none focus:border-indigo-400 focus:bg-white" />
          <button onClick={() => sendMessage()} disabled={!input.trim() || loading}
            className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center disabled:opacity-40 active:scale-90">
            <Send className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>
    </div>
  )
}
